import React, { useState } from "react";
import { PlayCircle, ExternalLink, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

// Returns an embeddable URL for YouTube / Vimeo / Loom, or null if the link can't be embedded.
export function toEmbedUrl(raw) {
  if (!raw) return null;
  const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  let u;
  try { u = new URL(href); } catch { return null; }
  const host = u.hostname.replace(/^www\.|^m\./, "");
  if (host === "youtu.be") {
    const id = u.pathname.slice(1).split("/")[0];
    return id ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0${startParam(u)}` : null;
  }
  if (host === "youtube.com" || host === "youtube-nocookie.com") {
    let id = u.searchParams.get("v");
    if (!id) {
      const m = u.pathname.match(/\/(shorts|embed|live)\/([^/?]+)/);
      id = m ? m[2] : null;
    }
    if (u.searchParams.get("list") && !id) return `https://www.youtube-nocookie.com/embed/videoseries?list=${u.searchParams.get("list")}`;
    return id ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0${startParam(u)}` : null;
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const m = u.pathname.match(/(\d{6,})/);
    return m ? `https://player.vimeo.com/video/${m[1]}?autoplay=1` : null;
  }
  if (host === "loom.com") {
    const m = u.pathname.match(/\/share\/([a-zA-Z0-9]+)/);
    return m ? `https://www.loom.com/embed/${m[1]}` : null;
  }
  return null;
}

function startParam(u) {
  const t = u.searchParams.get("t") || u.searchParams.get("start");
  if (!t) return "";
  const m = String(t).match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/);
  const secs = m ? (+(m[1] || 0)) * 3600 + (+(m[2] || 0)) * 60 + (+(m[3] || 0)) : parseInt(t, 10);
  return secs ? `&start=${secs}` : "";
}

export function VideoModal({ url, title, open, onClose }) {
  const embed = toEmbedUrl(url);
  const href = /^https?:\/\//i.test(url || "") ? url : `https://${url}`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl w-[95vw] p-0 bg-[#0a0a0a] border border-white/10 overflow-hidden [&>button]:hidden" data-testid="video-modal">
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/10 bg-[#101010]">
          <DialogTitle className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-bold text-white truncate">
            <PlayCircle className="w-4 h-4 text-[#ff4d4d] shrink-0" /> <span className="truncate">{title || "Drill video"}</span>
          </DialogTitle>
          <div className="flex items-center gap-1 shrink-0">
            <a href={href} target="_blank" rel="noopener noreferrer" className="p-2 text-[#a0a0a0] hover:text-white" title="Open in new tab" data-testid="video-modal-external"><ExternalLink className="w-4 h-4" /></a>
            <button onClick={onClose} className="p-2 text-[#a0a0a0] hover:text-white" data-testid="video-modal-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        {embed ? (
          <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
            <iframe
              src={embed}
              title={title || "Drill video"}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="video-modal-iframe"
            />
          </div>
        ) : (
          <div className="p-10 text-center text-sm text-[#a0a0a0]" data-testid="video-modal-fallback">
            This link can't be played inside the site.
            <a href={href} target="_blank" rel="noopener noreferrer" className="block mt-3 text-[var(--org-accent)] underline">Open it in a new tab →</a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Drop-in link: opens the modal for embeddable videos, falls back to a new tab otherwise.
export function VideoLink({ url, title, className = "", testId = "drill-video-link" }) {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  const embeddable = !!toEmbedUrl(url);
  const isYt = /youtu\.?be/i.test(href);
  const label = isYt ? "Watch video" : "Watch video";
  const cls = `inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold text-[#ff4d4d] hover:text-white transition-colors ${className}`;
  if (!embeddable) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={cls} data-testid={testId}>
        <PlayCircle className="w-3.5 h-3.5" /> {label}
      </a>
    );
  }
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} className={cls} data-testid={testId}>
        <PlayCircle className="w-3.5 h-3.5" /> {label}
      </button>
      {open && <VideoModal url={url} title={title} open={open} onClose={() => setOpen(false)} />}
    </>
  );
}
